import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable } from 'rxjs';

import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { IGetApiResponse } from 'v2/modules/shared/interfaces/api-response.interface';
import { ApiService } from 'v2/modules/shared/services/api/api.service';
import { EnvironmentUtilService } from 'v2/modules/shared/services/util/environment-util.service';
import { throwResponseAsLumResponseError } from 'v2/shared/api/lum-response-error';
import { IPaginatedResponse } from 'v2/shared/api/paginated-response.interface';

import { COST_LAYERS_API_DEFAULT_RELATIONS } from '../cost-layers.const';
import {
  ICostLayer,
  ICostLayerChange,
  ICostLayerEditFields,
  ICostLayerRepairJob,
  ICostLayerRepairResponse,
} from '../cost-layers.types';

@Injectable({ providedIn: 'root' })
export class CostLayersApiService {
  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private _feature :FeatureToggleService,
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  get(
    params ?:HttpParams | HttpParamsHash,
  ) :Observable<IGetApiResponse<ICostLayer[]>> {
    params = this.#fillParamsWithDefaultCostLayerRelations(params);

    if (!this._feature.isActive('utah-UTAH-271-cost_layer_serverside_multisort')) {
      return this.apiService
        .get(`${this.environment.urlV2}/cost-layers`, { params })
        .pipe(
          catchError(
            throwResponseAsLumResponseError(`Failed to fetch cost layers`),
          ),
        );
    }

    const urlTmp = '/v3/api/admin';

    return this.apiService
      .get(`${urlTmp}/cost-layers`, { params })
      .pipe(
        catchError(throwResponseAsLumResponseError(`Failed to fetch cost layers`)),
      );
  }

  getById(
    id :ICostLayer['id'],
    params ?:HttpParams | HttpParamsHash,
  ) :Observable<ICostLayer> {
    params = this.#fillParamsWithDefaultCostLayerRelations(params);

    return this.apiService
      .get(`${this.environment.urlV2}/cost-layers/${id}`, { params })
      .pipe(
        catchError(
          throwResponseAsLumResponseError(`Failed to fetch cost layer ${id}`),
        ),
      );
  }

  getChanges(
    id :ICostLayer['id'],
    params ?:HttpParams | HttpParamsHash,
  ) :Observable<IPaginatedResponse<ICostLayerChange>> {
    return this.apiService
      .get(`${this.environment.urlV2}/cost-layers/${id}/changes`, { params })
      .pipe(
        catchError(
          throwResponseAsLumResponseError(
            `Failed to fetch changes for cost layer ${id}`,
          ),
        ),
      );
  }

  getCostLayerByRfqWarehouse(
    rfqId :number,
    warehouseId :number,
    params ?:HttpParams | HttpParamsHash,
  ) :Observable<any> {
    params = this.#fillParamsWithDefaultCostLayerRelations(params);

    return this.apiService
      .get(
        `${this.environment.urlV2}/cost-layers-rfq/${rfqId}/warehouse/${warehouseId}`,
        { params },
      )
      .pipe(
        catchError(throwResponseAsLumResponseError(`Failed to fetch cost layers`)),
      );
  }

  updateCostLayer(
    id :ICostLayer['id'],
    body :ICostLayerEditFields,
  ) :Observable<{ data :ICostLayer }> {
    return this.apiService
      .post({
        path: `${this.environment.urlV2}/cost-layers/${id}`,
        body,
      })
      .pipe(
        catchError(
          throwResponseAsLumResponseError(`Failed to update cost layer ${id}`),
        ),
      );
  }

  updateCostLayerUnitCost(data :{
    costLayerId :number;
    costPerUnit :number;
    relations ?:string;
  }) :Observable<any> {
    const { costLayerId, costPerUnit, relations = '' } = data;

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/cost-layers/update-unit-cost`,
        body: { costPerUnit, costLayerId, relations },
      })
      .pipe(
        catchError(
          throwResponseAsLumResponseError(
            `Failed to update unit cost on cost layer ${costLayerId}`,
          ),
        ),
      );
  }

  repairCostLayers(costLayerIds ?:number[]) :Observable<ICostLayerRepairResponse> {
    const body = costLayerIds ? { cost_layer_ids: costLayerIds } : {};
    const url = `${this.environment.urlV2}/cost-layers/repair`;

    return this.apiService
      .post({
        path: url,
        body,
      })
      .pipe(
        catchError(throwResponseAsLumResponseError('Failed to repair cost layers')),
      );
  }

  getRepairJobs(
    params ?:HttpParams,
  ) :Observable<IPaginatedResponse<ICostLayerRepairJob>> {
    const url = `${this.environment.urlV2}/cost-layers/repair/jobs`;

    return this.apiService
      .get(url, { params })
      .pipe(
        catchError(throwResponseAsLumResponseError('Failed to fetch repair jobs')),
      );
  }

  getRepairJob(jobId :string) :Observable<ICostLayerRepairJob> {
    const url = `${this.environment.urlV2}/cost-layers/repair/jobs/${jobId}`;

    return this.apiService
      .get(url)
      .pipe(
        catchError(
          throwResponseAsLumResponseError(
            `Failed to fetch repair job status for ${jobId}`,
          ),
        ),
      );
  }

  #fillParamsWithDefaultCostLayerRelations(
    params :HttpParams | HttpParamsHash,
  ) :HttpParams | HttpParamsHash {
    const DEFAULT_RELATIONS = COST_LAYERS_API_DEFAULT_RELATIONS.join('|');

    if (params instanceof HttpParams) {
      if (!params.has('relations')) {
        params = params.set('relations', DEFAULT_RELATIONS);
      }
    } else {
      params = {
        relations: DEFAULT_RELATIONS,
        ...params,
      };
    }

    return params;
  }
}
